import * as React from 'react';
import Box from '@mui/material/Box';
import isSameDay from 'date-fns/isSameDay';
import { formatDate } from '../../../../../shared/LanguageService';
import { getBusinessHours, getSlotIndex } from './timelineCalendarHelperFuncitons';
import BookedEventItem from './TimelineCalendarBookedEventItem';
import BlockedEventItem from './TimelineCalendarBlockedEventItem';
import NewBookingEventItem from './TimelineCalendarNewBookingItem';
import { BookedEventType, CalendarRowSize, Order, Photographer } from '../../BookingsCalendarTypes';
import ICalendarItem from '../../../../calendar/Types';
import { ContainerSizeAndPosition } from '../../state/useCalendarContainerWidth';

type TimelineCalendarContentProps = {
    photographer: Photographer;
    isOnFirstRow: boolean;
    selectedOrder: Order;
    firstCalendarSlotForBooking: number;
    bookingInfo: Partial<ICalendarItem>;
    containerSizeAndPosition: ContainerSizeAndPosition;
    movedBookingIndex: number;
    newBookingLenght: number;
    isAdvancedMode: boolean;
    selectedDate: Date;
    onPreventResize: (preventResize: boolean) => void;
    onResizing: (isResizing: boolean) => void;
    hasNoEventsInCalendar: boolean;
    isActiveRow: boolean;
};

const availableEventStyle = (
    eventStartIndex,
    eventEndIndex,
    hasNewBookingInCalendar,
    hasNoEventsInCalendar,
    isActiveRow
) => {
    return {
        display: 'flex',
        position: 'absolute',
        gridColumnStart: eventStartIndex,
        gridColumnEnd: eventEndIndex,
        backgroundColor: 'rgba(189, 238, 164, 0.2)',
        height:
            hasNewBookingInCalendar || isActiveRow
                ? CalendarRowSize.large
                : hasNoEventsInCalendar
                ? CalendarRowSize.small
                : CalendarRowSize.medium,
        top: 0,
        left: 0,
        bottom: 0,
        right: 0
    };
};

const unAvailableEventStyle = (
    eventStartIndex,
    eventEndIndex,
    hasNewBookingInCalendar,
    hasNoEventsInCalendar,
    isActiveRow
) => {
    return {
        display: 'flex',
        position: 'absolute',
        backgroundColor: '',
        gridColumnStart: eventStartIndex,
        gridColumnEnd: eventEndIndex,
        height:
            hasNewBookingInCalendar || isActiveRow
                ? CalendarRowSize.large
                : hasNoEventsInCalendar
                ? CalendarRowSize.small
                : CalendarRowSize.medium,
        top: 0,
        left: 0,
        bottom: 0,
        right: 0
    };
};

const travelTimeEventStyle = (
    eventStartIndex,
    eventEndIndex,
    hasNewBookingInCalendar,
    hasNoEventsInCalendar,
    isActiveRow
) => {
    return {
        display: 'flex',
        position: 'absolute',
        backgroundColor: 'rgb(33, 150, 243,0.1)',
        gridColumnStart: eventStartIndex,
        gridColumnEnd: eventEndIndex,
        height:
            hasNewBookingInCalendar || isActiveRow
                ? CalendarRowSize.large
                : hasNoEventsInCalendar
                ? CalendarRowSize.small
                : CalendarRowSize.medium,
        top: 0,
        left: 0,
        bottom: 0,
        right: 0
    };
};

const TimelineCalendarContent = React.forwardRef(
    (
        {
            photographer,
            isOnFirstRow,
            selectedOrder,
            firstCalendarSlotForBooking,
            bookingInfo,
            containerSizeAndPosition,
            movedBookingIndex,
            newBookingLenght,
            isAdvancedMode,
            selectedDate,
            onPreventResize,
            onResizing,
            hasNoEventsInCalendar,
            isActiveRow
        }: TimelineCalendarContentProps,
        scrollRef: React.MutableRefObject<HTMLElement>
    ) => {
        let businessHours: Date[] = selectedDate && getBusinessHours(selectedDate);

        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {photographer.bookedEvents &&
                    photographer.bookedEvents
                        .filter((event) => isSameDay(new Date(event.start), selectedDate))
                        .map((event, index) => {
                            let formattedStartTime = event.start
                                ? formatDate(new Date(event.start), 'kk' + ':' + 'mm')
                                : null;
                            let formattedEndTime = event.end
                                ? formatDate(new Date(event.end), 'kk' + ':' + 'mm')
                                : null;

                            let eventStartIndex = event.start
                                ? Math.round(
                                      getSlotIndex(businessHours[0], new Date(event.start))
                                  ) + 1
                                : null;
                            let eventEndIndex = event.end
                                ? Math.round(getSlotIndex(businessHours[0], new Date(event.end))) +
                                  1
                                : null;

                            let unableToDisplayBooking: boolean =
                                eventStartIndex === null ||
                                eventEndIndex === null ||
                                (selectedOrder != null &&
                                    bookingInfo === null &&
                                    index === firstCalendarSlotForBooking);

                            if (unableToDisplayBooking) {
                                throw new Error('Calendar was unable to display booking');
                            }

                            let hasNewBookingInCalendar: boolean = selectedOrder != null;

                            return (
                                <Box key={index}>
                                    {event.type === BookedEventType.available ? (
                                        <Box key={event.id}>
                                            {event.orderId === null && (
                                                <Box
                                                    sx={availableEventStyle(
                                                        eventStartIndex,
                                                        eventEndIndex,
                                                        hasNewBookingInCalendar,
                                                        hasNoEventsInCalendar,
                                                        isActiveRow
                                                    )}
                                                />
                                            )}
                                            {selectedOrder != null &&
                                            bookingInfo != null &&
                                            index === firstCalendarSlotForBooking ? (
                                                <NewBookingEventItem
                                                    ref={scrollRef}
                                                    containerSizeAndPosition={
                                                        containerSizeAndPosition
                                                    }
                                                    newBookingEvent={selectedOrder}
                                                    isOnFirstRow={isOnFirstRow}
                                                    newBookingStartTime={bookingInfo.start}
                                                    newBookingEndTime={bookingInfo.end}
                                                    newBookingEventStartIndex={
                                                        movedBookingIndex
                                                            ? movedBookingIndex
                                                            : eventStartIndex
                                                    }
                                                    newBookingEventEndIndex={
                                                        movedBookingIndex
                                                            ? movedBookingIndex + newBookingLenght
                                                            : eventStartIndex + newBookingLenght
                                                    }
                                                    newBookingLenght={newBookingLenght}
                                                    photographer={photographer}
                                                    isAdvancedMode={isAdvancedMode}
                                                    onPreventResize={onPreventResize}
                                                    onResizing={onResizing}
                                                />
                                            ) : null}
                                        </Box>
                                    ) : null}
                                    {event.type === BookedEventType.se360Booking ||
                                    event.type === BookedEventType.scandphotosBooking ? (
                                        <Box
                                            key={event.id}
                                            sx={unAvailableEventStyle(
                                                eventStartIndex,
                                                eventEndIndex,
                                                hasNewBookingInCalendar,
                                                hasNoEventsInCalendar,
                                                isActiveRow
                                            )}
                                        >
                                            <BookedEventItem
                                                event={event}
                                                formattedStartTime={formattedStartTime}
                                                formattedEndTime={formattedEndTime}
                                                eventStartIndex={eventStartIndex}
                                                eventEndIndex={eventEndIndex}
                                                isOnFirstRow={isOnFirstRow}
                                                hasNewBookingInCalendar={hasNewBookingInCalendar}
                                                isActiveRow={isActiveRow}
                                            />
                                        </Box>
                                    ) : null}
                                    {event.type === BookedEventType.blocked ? (
                                        <Box
                                            key={event.id}
                                            sx={unAvailableEventStyle(
                                                eventStartIndex,
                                                eventEndIndex,
                                                hasNewBookingInCalendar,
                                                hasNoEventsInCalendar,
                                                isActiveRow
                                            )}
                                        >
                                            <BlockedEventItem
                                                event={event}
                                                formattedStartTime={formattedStartTime}
                                                formattedEndTime={formattedEndTime}
                                                eventStartIndex={eventStartIndex}
                                                eventEndIndex={eventEndIndex}
                                                isOnFirstRow={isOnFirstRow}
                                                hasNewBookingInCalendar={hasNewBookingInCalendar}
                                                isActiveRow={isActiveRow}
                                            />
                                        </Box>
                                    ) : null}
                                    {event.type === BookedEventType.travel &&
                                    eventStartIndex != eventEndIndex ? (
                                        <Box
                                            key={event.id}
                                            sx={travelTimeEventStyle(
                                                eventStartIndex,
                                                eventEndIndex,
                                                hasNewBookingInCalendar,
                                                hasNoEventsInCalendar,
                                                isActiveRow
                                            )}
                                        />
                                    ) : null}
                                </Box>
                            );
                        })}
            </Box>
        );
    }
);

export default TimelineCalendarContent;
